/**
 * Mini-editor de formato para el mensaje a compartir — un "wordpad" simple para
 * que el promotor componga con negrita/cursiva/cita y emojis SIN saber los símbolos.
 *
 * Formato de WhatsApp (y Telegram lo respeta casi igual): *negrita*, _cursiva_,
 * ~tachado~, ``` mono ```, y `> ` para cita al inicio de línea. El promotor
 * selecciona texto y toca un botón → se envuelve. El preview muestra cómo se verá.
 *
 * MÁQUINA DE ESTADOS DE FOCO / CARET / VIEWPORT (el corazón de un teclado custom
 * profesional — las teclas las dibuja cualquiera, esto es lo difícil):
 *
 *  1. Abrir sin teclado nativo: el textarea tiene inputmode="none" (estático en el
 *     HTML de compartir.js). En iOS sumamos el truco readOnly intermitente. Foco
 *     real → caret real, sin que el SO abra su teclado encima.
 *  2. Cerrar limpio: persistimos el caret ANTES de soltar; el cierre no lo destruye.
 *  3. Tocar fuera (no campo, no teclado): cierra conservando caret.
 *  4. Recuperar foco: si iOS suelta el foco (scroll/rotación), un listener de blur
 *     re-enfoca + restaura el caret en el próximo frame.
 *  5. Caret siempre correcto: se guarda en cada operación; se restaura al reentrar.
 *  6. No tapar el textarea: al abrir, scrollIntoView del teclado por encima del campo.
 *  7. Doble-tap: guard `tecladoEnTransicion` + touch-action:manipulation (CSS).
 *  8. iOS vs Android: feature-detect (VirtualKeyboard API / visualViewport), no UA.
 *  9. Rotar: visualViewport.resize re-asegura visibilidad + recupera foco.
 */
import { t } from '../languages.js';
import { crearTecladoPromotor } from './teclado-promotor.js';
import { crearPreviewMulticanal } from './preview-multicanal.js';

const TIENE_VIRTUAL_KEYBOARD_API = typeof navigator !== 'undefined' && 'virtualKeyboard' in navigator;
const MARGEN_SCROLL_PX = 16;

/**
 * Envuelve/desenvuelve (TOGGLE) la selección con `marca` (ej '*'). Si la selección
 * YA está envuelta con esa marca, la quita (evita *texto** basura por doble-tap o
 * re-aplicar). Si no, la envuelve. Sin selección: inserta las marcas con cursor
 * en medio. Es lo que distingue un editor pro de uno que duplica símbolos.
 */
function envolver(textarea, marca) {
    const ini = textarea.selectionStart;
    const fin = textarea.selectionEnd;
    const val = textarea.value;
    const sel = val.slice(ini, fin);
    const m = marca.length;

    // ¿La selección YA está envuelta con la marca? (la marca está justo afuera).
    const yaEnvueltaFuera = val.slice(ini - m, ini) === marca && val.slice(fin, fin + m) === marca;
    // ¿O la selección incluye las marcas en sus bordes?
    const yaEnvueltaDentro = sel.length >= 2 * m && sel.slice(0, m) === marca && sel.slice(-m) === marca;

    if (yaEnvueltaFuera) {
        // Quitar las marcas de afuera.
        textarea.value = val.slice(0, ini - m) + sel + val.slice(fin + m);
        textarea.setSelectionRange(ini - m, fin - m);
    } else if (yaEnvueltaDentro) {
        // Quitar las marcas de adentro de la selección.
        const limpio = sel.slice(m, -m);
        textarea.value = val.slice(0, ini) + limpio + val.slice(fin);
        textarea.setSelectionRange(ini, ini + limpio.length);
    } else {
        // Envolver.
        textarea.value = val.slice(0, ini) + marca + sel + marca + val.slice(fin);
        const pos = ini + m;
        const posFin = sel ? fin + m : ini + m;
        textarea.setSelectionRange(pos, posFin);
    }
    textarea.focus({ preventScroll: true });
}

/** Antepone "> " a la línea actual (cita estilo WhatsApp). */
function citarLinea(textarea) {
    const val = textarea.value;
    const ini = textarea.selectionStart;
    const inicioLinea = val.lastIndexOf('\n', ini - 1) + 1;
    textarea.value = val.slice(0, inicioLinea) + '> ' + val.slice(inicioLinea);
    const pos = ini + 2;
    textarea.setSelectionRange(pos, pos);
    textarea.focus({ preventScroll: true });
}

/**
 * Monta la barra de formato + preview alrededor de un textarea ya existente.
 * @param {HTMLTextAreaElement} textarea
 * @param {()=>void} onChange  callback cuando el texto cambia (para persistir)
 * @param {()=>string} obtenerLink  para el preview multicanal
 * @returns {HTMLElement} el contenedor de la barra (insertar antes del textarea)
 */
export function montarEditorMensaje(textarea, onChange, obtenerLink) {
    const wrap = document.createElement('div');
    wrap.className = 'pm-editor';
    wrap.innerHTML = `
        <div class="pm-editor-barra">
            <button type="button" class="pm-fmt-btn" data-fmt="*" title="${t('editor.negrita')}"><b>B</b></button>
            <button type="button" class="pm-fmt-btn" data-fmt="_" title="${t('editor.cursiva')}"><i>I</i></button>
            <button type="button" class="pm-fmt-btn" data-fmt="~" title="${t('editor.tachado')}"><s>S</s></button>
            <button type="button" class="pm-fmt-btn" data-cita="1" title="${t('editor.cita')}">❝</button>
        </div>`;

    // El teclado custom (nos da control: formato + emojis $ + acentos PT/ES) aparece
    // AUTOMÁTICO al tocar el campo y sale por DEBAJO, como el del OS. Sin botón raro.
    const tecladoHost = document.createElement('div');
    tecladoHost.className = 'pm-teclado-host';
    tecladoHost.id = 'pm-teclado-host';
    tecladoHost.hidden = true;
    let tecladoInstancia = null;
    let tecladoEnTransicion = false; // guard anti doble-tap (CLAUDE.md §1.9)
    let caretGuardado = { start: 0, end: 0 };

    // ── CARET: persistir / restaurar (5) ──────────────────────────────────
    function guardarCaret() {
        if (typeof textarea.selectionStart === 'number') {
            caretGuardado = { start: textarea.selectionStart, end: textarea.selectionEnd };
        }
    }
    function restaurarCaret() {
        if (document.activeElement !== textarea) textarea.focus({ preventScroll: true });
        try {
            textarea.setSelectionRange(caretGuardado.start, caretGuardado.end);
        } catch (_) {
            /* textarea detached: ignorar */
        }
    }

    // ── (8) Suprimir el teclado NATIVO sin matar el caret ─────────────────
    //    El textarea YA tiene inputmode="none" PERMANENTE (compartir.js): el SO no
    //    abre su teclado. Por eso NO usamos el truco readOnly: en iOS un textarea
    //    readOnly devuelve selectionStart=0 y NO muestra caret —rompería justo lo
    //    que queremos preservar—. Mantenerlo editable + inputmode=none da caret real
    //    sin teclado del SO. En Chromium, además, tomamos control oficial del VK para
    //    que el navegador no nos redimensione el viewport por su cuenta.
    function suprimirTecladoNativo() {
        if (TIENE_VIRTUAL_KEYBOARD_API) {
            try {
                navigator.virtualKeyboard.overlaysContent = true;
            } catch (_) {}
        }
    }
    function restaurarTecladoNativo() {
        if (TIENE_VIRTUAL_KEYBOARD_API) {
            try {
                navigator.virtualKeyboard.overlaysContent = false;
            } catch (_) {}
        }
    }

    // ── (6)/(9) Asegurar que el teclado custom (FIXED al fondo) NO tape el campo ──
    function alturaTecladoPx() {
        const r = tecladoHost.getBoundingClientRect();
        return r.height || 0;
    }
    // El teclado es position:fixed al fondo → tapa el contenido inferior. Reservamos
    // ese espacio con padding-bottom en el scroller del panel, para que el campo
    // pueda subir por encima del teclado (igual que el SO empuja el contenido).
    function scrollerDelPanel() {
        return document.querySelector('.promoter-surface') || document.body;
    }
    function asegurarVisible() {
        if (!tecladoInstancia) return;
        const alto = alturaTecladoPx();
        const scroller = scrollerDelPanel();
        scroller.style.paddingBottom = alto + MARGEN_SCROLL_PX + 'px';
        // Llevar el campo a la vista por encima del teclado.
        const rect = textarea.getBoundingClientRect();
        const limite = window.innerHeight - alto - MARGEN_SCROLL_PX;
        if (rect.bottom > limite) {
            try {
                textarea.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } catch (_) {}
        }
    }
    function liberarEspacioTeclado() {
        scrollerDelPanel().style.paddingBottom = '';
    }

    // ── Anclar el teclado al fondo REALMENTE visible. En iOS la barra del navegador
    //    mueve el visual viewport y bottom:0 no lo sigue. Con inputmode=none NO hay
    //    teclado del SO, así que offsetTop≈0 y height = alto visible bajo la barra.
    //    Setea --pm-vv-bottom (el CSS cuelga el teclado desde ahí con translateY).
    let frameAnclaProgramado = false;
    function aplicarAnclaTeclado() {
        frameAnclaProgramado = false;
        const vv = window.visualViewport;
        const bordeInferiorVisible = vv ? vv.offsetTop + vv.height : window.innerHeight;
        document.documentElement.style.setProperty('--pm-vv-bottom', bordeInferiorVisible + 'px');
    }
    function posicionarTeclado() {
        if (frameAnclaProgramado) return; // 1 recálculo por frame (hot path scroll)
        frameAnclaProgramado = true;
        requestAnimationFrame(aplicarAnclaTeclado);
    }

    // ── (4) Recuperar foco ante blur involuntario (iOS scroll/rotación/back) ──
    function onBlurInvoluntario() {
        if (!tecladoInstancia) return; // sólo nos importa con el teclado abierto
        // El blur NO lo pedimos nosotros → recuperar en el próximo frame.
        requestAnimationFrame(() => {
            if (!tecladoInstancia) return;
            restaurarCaret();
        });
    }

    // ── (3) Tap fuera del SISTEMA (campo + barra + teclado) → cerrar conservando
    //    caret. "Dentro" = la barra de formato (.pm-editor) O el TECLADO (.pm-teclado-
    //    host, que vive FUERA del wrap, debajo del campo) O el propio textarea. Tocar
    //    una tecla o Negrita NO debe cerrar — solo tocar afuera de todo.
    function onPointerdownDocumento(e) {
        if (!tecladoInstancia) return;
        const dentroDelEditor = e.target.closest('.pm-editor');
        const dentroDelTeclado = e.target.closest('.pm-teclado-host');
        const dentroDelCampo = e.target === textarea || e.target.closest('#pm-mensaje');
        if (dentroDelEditor || dentroDelTeclado || dentroDelCampo) return;
        cerrarTeclado();
    }

    // ── (9) Rotación / cambio de viewport ─────────────────────────────────
    function onViewportResize() {
        if (!tecladoInstancia) return;
        posicionarTeclado(); // reanclar al fondo visible (la barra del navegador se movió)
        if (document.activeElement !== textarea) restaurarCaret(); // iOS suelta foco al rotar
        asegurarVisible();
    }

    // ── ABRIR (1) ─────────────────────────────────────────────────────────
    function abrirTeclado() {
        if (tecladoInstancia) return;
        suprimirTecladoNativo();
        // El CSS oculta la nav inferior mientras el teclado está abierto (el teclado
        // cubre toda la franja inferior, como el del SO — sin competencia ni hueco).
        document.body.classList.add('pm-teclado-activo');
        tecladoInstancia = crearTecladoPromotor(textarea, () => {
            if (onChange) onChange();
            actualizarPreview();
        });
        tecladoHost.innerHTML = '';
        tecladoHost.appendChild(tecladoInstancia.el);
        tecladoHost.hidden = false;

        // Caret real: si nunca se posicionó (o está en 0), al final del texto.
        if (textarea.selectionStart == null || (textarea.selectionStart === 0 && textarea.selectionEnd === 0)) {
            const fin = textarea.value.length;
            caretGuardado = { start: fin, end: fin };
        } else {
            guardarCaret();
        }
        restaurarCaret();

        // Listeners de la máquina de estados (se quitan al cerrar).
        textarea.addEventListener('blur', onBlurInvoluntario);
        document.addEventListener('pointerdown', onPointerdownDocumento, true);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', onViewportResize);
            window.visualViewport.addEventListener('scroll', onViewportResize);
        }
        // Anclar al fondo visible real + seguir el scroll de página (barra navegador iOS).
        posicionarTeclado();
        window.addEventListener('scroll', posicionarTeclado, { passive: true });

        // (6) No tapar el campo — tras pintar el teclado, scrollearlo a la vista.
        requestAnimationFrame(asegurarVisible);
    }

    // ── CERRAR (2): persiste caret, quita listeners, restaura nativo ──────
    function cerrarTeclado() {
        if (!tecladoInstancia) return;
        guardarCaret(); // el cierre NO destruye el caret
        tecladoInstancia.destroy();
        tecladoInstancia = null;
        tecladoHost.hidden = true;
        tecladoHost.innerHTML = '';
        document.body.classList.remove('pm-teclado-activo'); // restaura la nav inferior
        liberarEspacioTeclado(); // quitar el padding-bottom reservado para el teclado fixed

        textarea.removeEventListener('blur', onBlurInvoluntario);
        document.removeEventListener('pointerdown', onPointerdownDocumento, true);
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', onViewportResize);
            window.visualViewport.removeEventListener('scroll', onViewportResize);
        }
        window.removeEventListener('scroll', posicionarTeclado);
        document.documentElement.style.removeProperty('--pm-vv-bottom');
        restaurarTecladoNativo();
        // Soltamos el foco para que el caret no quede parpadeando "muerto" sin
        // teclado; el caret persistido reaparece si el usuario reabre.
        textarea.blur();
    }

    // Abrir con guard anti doble-tap (7): tocar el campo dos veces rápido no debe
    // abrir/recrear dos teclados.
    function abrirTecladoSeguro() {
        if (tecladoInstancia || tecladoEnTransicion) return;
        tecladoEnTransicion = true;
        try {
            abrirTeclado();
        } finally {
            requestAnimationFrame(() => {
                tecladoEnTransicion = false;
            });
        }
    }

    // ── TRIGGER NATURAL: tocar el campo ABRE el teclado custom (como el del OS sale
    //    al enfocar un input). Si ya está abierto, solo reposiciona el caret (11).
    //    NO preventDefault (el tap debe mover el caret), NO readOnly (rompe iOS),
    //    inputmode="none" evita que el SO abra SU teclado encima del nuestro.
    textarea.addEventListener('click', () => {
        if (tecladoInstancia) {
            guardarCaret(); // ya abierto: el tap reposiciona el caret
        } else {
            abrirTecladoSeguro(); // cerrado: tocar el campo lo abre
        }
    });
    // focus como respaldo (iOS a veces enfoca sin emitir click fiable, ej. tab/
    // navegación). El guard de abrirTecladoSeguro evita reabrir si ya está montado
    // (el propio teclado custom hace textarea.focus() internamente — no debe loopear).
    textarea.addEventListener('focus', () => {
        if (!tecladoInstancia) abrirTecladoSeguro();
    });
    // `select` cubre el caso clave: el usuario ARRASTRA para seleccionar texto y
    // luego toca Negrita. El botón roba el foco al hacer click; sin esto, el caret
    // restaurado sería el viejo y se envolvería la posición equivocada. Guardando en
    // cada cambio de selección, caretGuardado SIEMPRE refleja la selección viva.
    textarea.addEventListener('select', guardarCaret);
    // Teclado físico (desktop / bluetooth): inputmode=none NO bloquea el tipeo real.
    // Mantener el caret persistido al vuelo para que reabrir el custom sea consistente.
    textarea.addEventListener('keyup', guardarCaret);

    // ── Barra de formato (B/I/S/❝) — el teclado ya no tiene botón (abre al tocar
    //    el campo). El click se delega en el wrap.
    wrap.addEventListener('click', e => {
        const fmt = e.target.closest('[data-fmt]');
        const cita = e.target.closest('[data-cita]');
        if (fmt) {
            // Con el teclado custom abierto, escribir formato preserva caret.
            if (tecladoInstancia) restaurarCaret();
            envolver(textarea, fmt.dataset.fmt);
            guardarCaret();
        } else if (cita) {
            if (tecladoInstancia) restaurarCaret();
            citarLinea(textarea);
            guardarCaret();
        } else {
            return;
        }
        if (onChange) onChange();
        actualizarPreview();
    });

    // Preview MULTICANAL: muestra cómo se verá en WhatsApp / Telegram / Redes
    // (con el formato adaptado por canal). Es el corazón de la herramienta.
    const previewMC = crearPreviewMulticanal();
    const lblPrevia = document.createElement('div');
    lblPrevia.className = 'pm-preview-lbl';
    lblPrevia.textContent = t('editor.previa');
    function actualizarPreview() {
        const txt = textarea.value || '';
        previewMC.actualizar(txt, obtenerLink ? obtenerLink() : '');
    }
    textarea.addEventListener('input', actualizarPreview);
    actualizarPreview();

    // Orden visual: barra (wrap, antes del textarea) → textarea → preview.
    textarea.insertAdjacentElement('afterend', previewMC.el);
    textarea.insertAdjacentElement('afterend', lblPrevia);
    // El TECLADO se portaliza a <body> (NO dentro de #promoter-app, que es max-width
    // 540px y jaula de stacking-context): así el z-index gana a la nav y el width:100vw
    // es full-width real. Se posiciona por JS (posicionarTeclado) al fondo VISIBLE.
    document.body.appendChild(tecladoHost);

    // TEARDOWN: como el host vive en <body> y la vista se cambia con surface.innerHTML
    // = '' (app.js), el wrap se desconecta pero el host quedaría HUÉRFANO en <body>.
    // Un observer lo detecta y limpia (cierra el teclado + remueve el host).
    const observerTeardown = new MutationObserver(() => {
        if (!wrap.isConnected) {
            cerrarTeclado();
            tecladoHost.remove();
            observerTeardown.disconnect();
        }
    });
    // Observamos el contenedor del panel (donde ocurre el innerHTML='').
    const raizPanel = document.querySelector('.promoter-surface') || document.body;
    observerTeardown.observe(raizPanel, { childList: true, subtree: true });

    return wrap;
}
